import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';

function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function parseSearchDate(value) {
  const trimmed = String(value || '').trim();
  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return trimmed;
  const br = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (br) return `${br[3]}-${br[2]}-${br[1]}`;
  return '';
}

function toDateInputValue(value) {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
}

function formatBirthDateDisplay(value) {
  const iso = toDateInputValue(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

function formatCpf(value) {
  return onlyDigits(value).slice(0, 11);
}

function normalizeSearchText(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function personLabel(person) {
  if (!person) return '';
  const parts = [];
  const cpf = person.companion_document || person.cpf;
  if (cpf) parts.push(`CPF ${formatCpf(cpf)}`);
  const birth = formatBirthDateDisplay(person.companion_birth_date || person.birth_date);
  if (birth) parts.push(birth);
  const mother = person.companion_mother_name || person.mother_name;
  if (mother) parts.push(`mãe: ${mother}`);
  if (person.source === 'esus') parts.push('e-SUS');
  if (person.source === 'local' || person.id) parts.push('cadastrado');
  return parts.join(' · ');
}

function candidateToResult(candidate) {
  const record = candidate.record || {};
  return {
    key: candidate.key,
    source: candidate.source,
    id: record.existing_id || record.id || null,
    ...record,
  };
}

function toCompanionPayload(person) {
  if (!person) return null;

  // Resposta do mode=companion da API já vem mapeada em companion_*
  if (person.companion_name && !person.name) {
    return {
      companion_document: formatCpf(person.companion_document || ''),
      companion_name: person.companion_name || '',
      companion_phone: person.companion_phone || '',
      companion_birth_date: toDateInputValue(person.companion_birth_date) || '',
    };
  }

  // Pessoa do cadastro / e-SUS selecionada como acompanhante
  return {
    companion_document: formatCpf(person.cpf || ''),
    companion_name: person.name || '',
    companion_phone: person.mobile_phone || person.contact_phone || '',
    companion_birth_date: toDateInputValue(person.birth_date) || '',
  };
}

export default function CompanionLookupSelect({
  companionDocument = '',
  companionName = '',
  companionPhone = '',
  companionBirthDate = '',
  onSelect,
  onClear,
  inputClassName,
}) {
  const selected = companionName || companionDocument
    ? {
      name: companionName || 'Acompanhante',
      companion_document: companionDocument,
      companion_phone: companionPhone,
      companion_birth_date: companionBirthDate,
    }
    : null;

  const [term, setTerm] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [esusHit, setEsusHit] = useState(null);
  const lastLookupCpfRef = useRef('');
  const requestIdRef = useRef(0);

  useEffect(() => {
    const dateFromTerm = parseSearchDate(term);
    const query = dateFromTerm ? '' : term.trim();
    const effectiveBirthDate = birthDate || dateFromTerm;
    const canSearch = query.length >= 2 || Boolean(effectiveBirthDate);
    if (!canSearch) {
      setResults([]);
      setEsusHit(null);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        let nextResults = [];

        if (effectiveBirthDate) {
          const response = await apiService.lookupPatientsByBirthDate(effectiveBirthDate);
          if (requestId !== requestIdRef.current) return;
          nextResults = (response.data?.candidates || []).map(candidateToResult);
          if (query) {
            const normalized = normalizeSearchText(query);
            const digits = onlyDigits(query);
            nextResults = nextResults.filter((person) => {
              const nameMatch = normalizeSearchText(person.name).includes(normalized);
              const motherMatch = normalizeSearchText(person.mother_name).includes(normalized);
              const cpfMatch = digits && formatCpf(person.cpf).includes(digits);
              return nameMatch || motherMatch || cpfMatch;
            });
          }
        } else {
          const response = await apiService.listTransport('patients', {
            search: query,
            limit: 20,
            page: 1,
          });
          if (requestId !== requestIdRef.current) return;
          nextResults = (response.success ? (response.data?.results || []) : []).map((patient) => ({
            ...patient,
            key: `local-${patient.id}`,
            source: 'local',
          }));
        }

        setResults(nextResults);
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.error('Erro ao buscar acompanhantes:', error);
        setResults([]);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [term, birthDate]);

  useEffect(() => {
    const digits = onlyDigits(term);
    if (digits.length !== 11) {
      lastLookupCpfRef.current = '';
      setEsusHit(null);
      return undefined;
    }
    if (lastLookupCpfRef.current === digits) return undefined;

    let cancelled = false;
    lastLookupCpfRef.current = digits;

    (async () => {
      try {
        setLookingUp(true);
        const response = await apiService.lookupEsusCidadaoByCpf(digits, 'companion');
        if (cancelled || !response.success || !response.data) return;

        if (response.data.source === 'local') {
          setEsusHit(null);
          setResults((prev) => {
            const payload = {
              key: `local-cpf-${digits}`,
              source: 'local',
              id: response.data.existing_id || null,
              name: response.data.companion_name || response.data.name,
              cpf: response.data.companion_document || response.data.cpf || digits,
              birth_date: response.data.companion_birth_date || response.data.birth_date,
              mother_name: response.data.companion_mother_name || response.data.mother_name,
              mobile_phone: response.data.companion_phone || response.data.mobile_phone,
              ...response.data,
            };
            if (prev.some((item) => formatCpf(item.cpf || item.companion_document) === digits)) {
              return prev;
            }
            return [payload, ...prev];
          });
          return;
        }

        setEsusHit(response.data);
      } catch (error) {
        if (!cancelled) {
          setEsusHit(null);
        }
      } finally {
        if (!cancelled) setLookingUp(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [term]);

  const emptyMessage = useMemo(() => {
    if (loading || lookingUp) return 'Buscando...';
    if (term.trim().length < 2 && !birthDate) return 'Digite o nome, CPF ou a data de nascimento (dd/mm/aaaa).';
    return 'Nenhum cidadão encontrado no cadastro local ou no e-SUS.';
  }, [birthDate, loading, lookingUp, term]);

  const applyPerson = (person) => {
    const payload = toCompanionPayload(person);
    if (!payload?.companion_name && !payload?.companion_document) {
      toast.error('Não foi possível carregar os dados do acompanhante');
      return;
    }
    onSelect?.(payload);
    toast.success(
      person?.source === 'local'
        ? 'Acompanhante encontrado no cadastro local'
        : 'Dados do acompanhante preenchidos'
    );
    setTerm('');
    setBirthDate('');
    setResults([]);
    setEsusHit(null);
  };

  if (selected) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-border bg-slate-50 px-3 py-2.5">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-text">{selected.name}</div>
          <div className="truncate text-xs text-muted">
            {[
              selected.companion_document ? `CPF ${formatCpf(selected.companion_document)}` : null,
              formatBirthDateDisplay(selected.companion_birth_date) || null,
              selected.companion_phone || null,
            ].filter(Boolean).join(' · ') || 'Sem CPF/nascimento'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            onClear?.();
            setTerm('');
            setBirthDate('');
            setResults([]);
            setEsusHit(null);
          }}
          className="inline-flex items-center gap-1 rounded-lg border border-border bg-white px-2.5 py-1.5 text-xs font-medium text-muted hover:bg-slate-50 hover:text-text"
        >
          <X size={12} />
          Alterar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[160px_minmax(0,1fr)]">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted">Nascimento</label>
          <input
            type="date"
            value={birthDate}
            onChange={(event) => setBirthDate(event.target.value)}
            className={inputClassName}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted">Nome, CPF ou nascimento</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              type="text"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder="Nome, CPF ou data (dd/mm/aaaa)"
              className={`${inputClassName} pl-9`}
            />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-white">
        <ul className="max-h-52 overflow-y-auto py-1 text-sm">
          {results.map((person) => (
            <li key={person.key || person.id || `${person.cpf}-${person.name}`}>
              <button
                type="button"
                onClick={() => applyPerson(person)}
                className="w-full px-3 py-2 text-left hover:bg-primary/5"
              >
                <div className="font-medium text-text">
                  {person.companion_name || person.name}
                  {person.source === 'esus' && !person.id ? (
                    <span className="ml-2 text-xs font-medium text-primary">e-SUS</span>
                  ) : null}
                </div>
                <div className="text-xs text-muted">{personLabel(person) || 'Sem CPF/nascimento'}</div>
              </button>
            </li>
          ))}

          {esusHit && (
            <li className="border-t border-border">
              <button
                type="button"
                onClick={() => applyPerson(esusHit)}
                className="w-full px-3 py-2 text-left hover:bg-primary/5"
              >
                <div className="font-medium text-primary">
                  Encontrado no e-SUS: {esusHit.companion_name || esusHit.name}
                </div>
                <div className="text-xs text-muted">
                  {formatBirthDateDisplay(esusHit.companion_birth_date || esusHit.birth_date) || 'Sem nascimento'}
                  {' · '}
                  selecionar acompanhante
                </div>
              </button>
            </li>
          )}

          {results.length === 0 && !esusHit && (
            <li className="px-3 py-2 text-muted">{emptyMessage}</li>
          )}
        </ul>
      </div>
    </div>
  );
}
